import { useLazyQuery } from '@apollo/client';
import { AFPTable, Pagination, StatusBadge } from '@gsa/afp-component-library';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDidMountEffect from '../../hooks/use-did-mount';
import { GET_ASSOCIATED_STANDARD } from '../../services/data-store.gql';
import { useEquipmentCodes } from './equipment-codes-provider';

const DEFAULT_ORDER = [['standardItem', 'standardItemNumber', 'ASC']];

const AssociatedStandardItemsTable = () => {
  const navigate = useNavigate();

  const { showFormModal, selectedEquipmentCode: selectedItem } =
    useEquipmentCodes();

  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [paginationState, setPaginationState] = useState({
    limit: 10,
    offset: 0,
    currentPage: 1,
  });

  const [getData, { data }] = useLazyQuery(GET_ASSOCIATED_STANDARD, {
    fetchPolicy: 'network-only',
  });

  const fetchPage = () => {
    const { limit, offset } = paginationState;
    getData({
      variables: {
        filters: {
          operator: 'EQ',
          key: 'equipmentCodeId',
          value: Number(selectedItem?.id),
        },
        limit,
        offset,
        order,
      },
    });
  };

  useEffect(() => {
    // Don't fire getData if modal is not open.
    if (!showFormModal || !selectedItem?.id) return;
    fetchPage();
  }, [showFormModal]);

  useDidMountEffect(() => {
    if (!showFormModal || !selectedItem?.id) return;
    fetchPage();
  }, [paginationState, order]);

  const handlePaginationChange = (currentPage, itemsPerPage) => {
    setPaginationState({
      limit: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage,
      currentPage,
    });
  };

  // The association query returns every standard item field the table needs,
  // so no follow-up standard item lookup is required.
  const responseData = data?.getEquipmentAssociations?.rows || [];
  const itemsCount = data?.getEquipmentAssociations?.count || 0;

  const columns = useMemo(
    () => [
      {
        Header: 'Standard Item',
        accessor: 'standardItem.standardItemNumber',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value, row }) =>
          value ? (
            <button
              type="button"
              className="usa-button usa-button--unstyled standard-item-view-button"
              onClick={() => {
                // eslint-disable-next-line react/prop-types
                const standardItem = row.original.standardItem;
                const year = standardItem?.year ?? selectedItem?.year;
                navigate(`/catalog/standard-item/${value}-${year}`, {
                  state: { ...standardItem, year },
                });
              }}
            >
              {value}
            </button>
          ) : null,
      },
      {
        Header: 'Vehicle Type',
        accessor: 'standardItem.vehicleTypeCode.title',
        sortable: false,
      },
      {
        Header: 'Title',
        accessor: 'standardItem.title',
      },
      {
        Header: 'Status',
        accessor: 'standardItem.isActive',
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => (
          <StatusBadge variant={value === 1 ? 'Ready-Gray' : 'Inactive-Gray'}>
            {value === 1 ? 'Active' : 'Inactive'}
          </StatusBadge>
        ),
      },
    ],
    [selectedItem?.year],
  );

  return (
    <>
      <AFPTable
        columns={columns}
        data={responseData}
        onSort={setOrder}
        defaultSort={order}
        fullWidth
      />
      {itemsCount > 0 && (
        <Pagination
          itemsPerPageOptions={[10, 25, 50]}
          onPageChange={handlePaginationChange}
          variant="advanced"
          itemsPerPage={paginationState.limit}
          currentPage={paginationState.currentPage}
          itemsCount={itemsCount}
        />
      )}
    </>
  );
};

export default AssociatedStandardItemsTable;
